import type { MapBox } from './map.js';

/**
 * In-editor map edits on a 1u grid. Carving subtracts a cell from any boxes it
 * intersects (splitting them); placing adds a 1u block; a stair edit expands
 * into a climbable flight. Edits are applied (in order) on top of the base map
 * by {@link applyMapEdits}, so the client meshes AND the server colliders both
 * see them. The dev editor writes the saved list to mapEdits.generated.ts.
 */

export type Dir = 0 | 1 | 2 | 3; // +x, -x, +z, -z

export interface MapEdit {
  op: 'carve' | 'place' | 'stair';
  /** Cell min corner on the 1u grid (integers). */
  x: number;
  y: number;
  z: number;
  dir?: Dir; // stairs only
  steps?: number; // stairs only
}

const EDIT_COLOR: [number, number, number] = [0.62, 0.63, 0.67];

interface AABB {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
  color: [number, number, number];
  label?: string;
}

const EPS = 1e-4;

function boxToAabb(b: MapBox): AABB {
  const y0 = b.y0 ?? 0;
  return {
    x0: b.cx - b.sx / 2,
    x1: b.cx + b.sx / 2,
    y0,
    y1: y0 + b.h,
    z0: b.cz - b.sz / 2,
    z1: b.cz + b.sz / 2,
    color: b.color,
    label: b.label,
  };
}

function aabbToBox(a: AABB): MapBox {
  return {
    cx: (a.x0 + a.x1) / 2,
    cz: (a.z0 + a.z1) / 2,
    sx: a.x1 - a.x0,
    sz: a.z1 - a.z0,
    h: a.y1 - a.y0,
    y0: a.y0,
    color: a.color,
    label: a.label,
  };
}

/** b minus the cell c → up to 6 remainder boxes (empty pieces dropped). */
function subtract(b: AABB, c: AABB): AABB[] {
  // No overlap → unchanged.
  if (b.x1 <= c.x0 || b.x0 >= c.x1 || b.y1 <= c.y0 || b.y0 >= c.y1 || b.z1 <= c.z0 || b.z0 >= c.z1) {
    return [b];
  }
  const ox0 = Math.max(b.x0, c.x0);
  const ox1 = Math.min(b.x1, c.x1);
  const oy0 = Math.max(b.y0, c.y0);
  const oy1 = Math.min(b.y1, c.y1);
  const oz0 = Math.max(b.z0, c.z0);
  const oz1 = Math.min(b.z1, c.z1);
  const out: AABB[] = [];
  const piece = (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number): void => {
    if (x1 - x0 > EPS && y1 - y0 > EPS && z1 - z0 > EPS) {
      out.push({ x0, x1, y0, y1, z0, z1, color: b.color, label: b.label });
    }
  };
  piece(b.x0, b.x1, b.y0, oy0, b.z0, b.z1); // below
  piece(b.x0, b.x1, oy1, b.y1, b.z0, b.z1); // above
  piece(b.x0, ox0, oy0, oy1, b.z0, b.z1); // -x slab
  piece(ox1, b.x1, oy0, oy1, b.z0, b.z1); // +x slab
  piece(ox0, ox1, oy0, oy1, b.z0, oz0); // -z slab
  piece(ox0, ox1, oy0, oy1, oz1, b.z1); // +z slab
  return out;
}

/** Expand a stair edit into climbable thin treads (0.3 rise, 1u run, 1u wide). */
function stairBoxes(e: MapEdit): AABB[] {
  const steps = e.steps ?? 10;
  const dir = e.dir ?? 0;
  const out: AABB[] = [];
  for (let i = 0; i < steps; i++) {
    const top = e.y + 0.3 * (i + 1);
    const along = i;
    let x0 = e.x;
    let x1 = e.x + 1;
    let z0 = e.z;
    let z1 = e.z + 1;
    if (dir === 0) { x0 = e.x + along; x1 = x0 + 1; } // +x
    else if (dir === 1) { x1 = e.x + 1 - along; x0 = x1 - 1; } // -x
    else if (dir === 2) { z0 = e.z + along; z1 = z0 + 1; } // +z
    else { z1 = e.z + 1 - along; z0 = z1 - 1; } // -z
    out.push({ x0, x1, y0: top - 0.3, y1: top, z0, z1, color: EDIT_COLOR, label: 'concrete' });
  }
  return out;
}

/** Apply edits (in order) to a base box list, returning the edited list. */
export function applyMapEdits(base: MapBox[], edits: MapEdit[]): MapBox[] {
  if (edits.length === 0) return base.slice();
  let aabbs = base.map(boxToAabb);
  for (const e of edits) {
    if (e.op === 'carve') {
      const c: AABB = { x0: e.x, x1: e.x + 1, y0: e.y, y1: e.y + 1, z0: e.z, z1: e.z + 1, color: EDIT_COLOR };
      aabbs = aabbs.flatMap((b) => subtract(b, c));
    } else if (e.op === 'place') {
      aabbs.push({ x0: e.x, x1: e.x + 1, y0: e.y, y1: e.y + 1, z0: e.z, z1: e.z + 1, color: EDIT_COLOR, label: 'concrete' });
    } else if (e.op === 'stair') {
      for (const s of stairBoxes(e)) aabbs.push(s);
    }
  }
  return aabbs.map(aabbToBox);
}
